import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { DataStudioHelperService } from 'app/data-studio-redesign/services/data-studio-helper.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DimensionService } from '../../../analyze/service/dimension-service/dimension.service';
import { NavigationComponentConfig } from '../navigation/navigation.component';
import { NavigationCategory, NavigationService } from 'app/shared/services/data-services/navigation/navigation.service';
import { Turbine } from 'app/analyze/service/dimension-service/class/turbine';
import { Park } from 'app/analyze/service/dimension-service/class/park';
import { Portfolio } from 'app/analyze/service/dimension-service/class/portfolio';
import { UserPreferencesPortfolioService } from 'app/shared/services/data-services/user-preferences-portfolio/user-preferences-portfolio.service';
import { IHeaderResponsivenessOptions } from 'app/shared/models/header-display-options';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: false,
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Input() config: NavigationComponentConfig;
  @Input() isSmallerDevice: boolean;

  public smallNav: boolean = false;
  public mobileNav: boolean = false;

  public fileReady = false;
  public noDataStudio;
  private unsubscribe$ = new Subject();

  public headerOpen: boolean;
  public defaultShown = true;
  public fileName = null;
  public hideNav = true;
  public submenuOpen = false;
  public portfolioShown = false;
  public parksShown = false;
  public turbinesShown = false;

  public selectedPortfolio: Portfolio;
  public selectedPark: Park;
  public selectedTurbine: Turbine;
  public selectedLevel: string;
  private firstTurbineFromPark: string;

  public parks = [];
  public portfolios = [];
  public turbines = [];
  headerDropdownColorType: any;
  public headerResponsivenessOptions: IHeaderResponsivenessOptions = {
    hideAssetsNavigation: false,
    hideTimezoneSwitch: false,
    collapseUserSettingsMenu: false,
    hideColorCodeLegend: false,
  };

  private ASSETS_MENU_HIDE_BREAKPOINT = 1024;
  private TIMEZONE_SWITCH_HIDE_BREAKPOINT = 1255;
  private COLOR_LEGEND_HIDE_BREAKPOINT = 1300;
  private COLLAPSE_USER_SETTINGS_BREAKPOINT = 1600;
  moduleName: string;

  public get hidePortfolio() {
    return !this.navigationService.isNavigationLevelActivated('portfolio', this.moduleName);
  }

  public get hidePark() {
    return !this.navigationService.isNavigationLevelActivated('park', this.moduleName);
  }

  public get hideTurbine() {
    return !this.navigationService.isNavigationLevelActivated('turbine', this.moduleName);
  }

  public get statusColor(): number | null {
    //reflect logic from src\app\monitor\turbine\turbine-p-1\turbine-monitor.component.ts
    if (this.moduleName !== 'monitor' || this.selectedLevel !== 'turbine' || !this.selectedTurbine) return null;
    const turbine = this.turbines.find(t => t._id === this.selectedTurbine._id);
    if (!turbine || turbine.status === undefined) return null;
    return turbine.status === null ? 8 : turbine.status;
  }

  constructor(
    private route: Router,
    private el: ElementRef,
    private navigationService: NavigationService,
    private dimensionService: DimensionService,
    private userPreferencesPortfolioService: UserPreferencesPortfolioService,
    private dataStudioHelper: DataStudioHelperService,
  ) {}

  @HostListener('window:resize')
  public onResize(): void {
    this.headerResponsivenessOptions = {
      hideAssetsNavigation: window.innerWidth < this.ASSETS_MENU_HIDE_BREAKPOINT,
      hideTimezoneSwitch: window.innerWidth < this.TIMEZONE_SWITCH_HIDE_BREAKPOINT,
      collapseUserSettingsMenu: window.innerWidth < this.COLLAPSE_USER_SETTINGS_BREAKPOINT,
      hideColorCodeLegend: window.innerWidth < this.COLOR_LEGEND_HIDE_BREAKPOINT,
    };

    this.smallNav = this.el.nativeElement.offsetWidth < 1350;
    this.mobileNav = this.el.nativeElement.offsetWidth < 900;
  }

  ngOnInit() {
    this.subscribeOnPortfolioResult();
    this.dataStudioHelper
      .getDateObservable$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(dateData => {
        if (dateData.clear === 'clear') {
          this.fileName = dateData.fileName;
          this.noDataStudio = window.location.pathname.includes('data_studio') ? false : true;
          this.fileReady = true;
        }
      });

    this.navigationService
      .getNavigationObservable([
        NavigationCategory.PortfolioLevelSelectedPortfolio,
        NavigationCategory.ParkLevelSelectedPark,
        NavigationCategory.TurbineLevelSelectedTurbine,
        NavigationCategory.Module,
        NavigationCategory.Level,
      ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(navigationDetails => {
        this.moduleName = navigationDetails.module;
        this.selectedPortfolio = this.dimensionService.portfolioList.find(
          p => p.id === navigationDetails.selectedPortfolioId,
        );
        this.selectedPark = this.dimensionService.parkList.find(p => p.id === navigationDetails.selectedParkId);
        this.selectedTurbine = this.dimensionService.turbineList.find(
          t => t._id === navigationDetails.selectedTurbineId,
        );
        this.selectedLevel = navigationDetails.level;
        this.firstTurbineFromPark =
          this.selectedLevel === 'park' &&
          this.dimensionService.turbineList.find(o => o.id_windpark === this.selectedPark.id)?._id;

        this.headerOpen = false;
        this.hideNav = this.navigationService.modulesWithoutNavigation.includes(navigationDetails.module);
        this.hideAll();
      });
    setTimeout(() => {
      // take some time to compute the width of the element
      this.onResize();
    });
  }

  public close() {
    this.fileReady = false;
  }

  public goToDataStudio() {
    this.route.navigateByUrl('data_studio');
    this.fileReady = false;
  }

  public hideAll(): void {
    this.submenuOpen = false;
    this.parksShown = false;
    this.turbinesShown = false;
    this.portfolioShown = false;
  }

  public toggleHeader() {
    this.headerOpen = !this.headerOpen;
    if (this.parksShown || this.turbinesShown || this.portfolioShown) {
      this.defaultShown = true;
      this.parksShown = false;
      this.turbinesShown = false;
      this.portfolioShown = false;
    }
  }

  public showParks(): void {
    if (this.parksShown) {
      this.submenuOpen = false;
      this.parksShown = false;
      return;
    }
    this.submenuOpen = true;
    this.parksShown = true;
    this.turbinesShown = false;
    this.portfolioShown = false;
  }

  public showPortfolio(): void {
    if (this.portfolioShown) {
      this.submenuOpen = false;
      this.portfolioShown = false;
      return;
    }
    this.submenuOpen = true;
    this.parksShown = false;
    this.turbinesShown = false;
    this.portfolioShown = true;
  }

  public showTurbines(): void {
    if (this.turbinesShown) {
      this.submenuOpen = false;
      this.turbinesShown = false;
      return;
    }
    this.submenuOpen = true;
    this.parksShown = false;
    this.turbinesShown = true;
    this.portfolioShown = false;
  }

  public navigateToPark(park: Park) {
    if ((this.selectedPark && this.selectedPark.id === park.id && this.selectedLevel === 'park') || this.hidePark)
      return;
    this.parksShown = false;
    this.defaultShown = true;
    this.toggleHeader();
    const turbines = this.turbines.filter(t => t.id_windpark === park.id).map(t => t._id);
    this.navigationService.redirectToPowerplant(park.id, turbines);
  }

  public navigateToTurbine(turbine: Turbine) {
    if (
      (this.selectedTurbine && this.selectedTurbine._id === turbine._id && this.selectedLevel === 'turbine') ||
      this.hideTurbine
    )
      return;
    this.turbinesShown = false;
    this.defaultShown = true;
    this.toggleHeader();
    this.navigationService.redirectToTurbine(turbine._id);
  }

  public navigateToPortfolio(portfolio: Portfolio) {
    if (
      (this.selectedPortfolio && this.selectedPortfolio.id === portfolio.id && this.selectedLevel === 'portfolio') ||
      this.hidePortfolio
    )
      return;
    this.portfolioShown = false;
    this.defaultShown = true;
    this.toggleHeader();
    this.navigationService.redirectToPortfolio(portfolio.id, portfolio.parks);
  }

  async subscribeOnPortfolioResult() {
    const portfoliosPromise = this.dimensionService.getPortfolioList();
    const parksPromise = this.dimensionService.getParkList();
    const turbinesPromise = this.dimensionService.getTurbineList();
    const [portfolios, parks, turbines] = await Promise.all([portfoliosPromise, parksPromise, turbinesPromise]);
    this.portfolios = portfolios;
    this.parks = parks.map(p => Object.assign({}, p));
    this.turbines = turbines.map(t => Object.assign({}, t));

    this.userPreferencesPortfolioService
      .getUserPortfoliosWithParksAndTurbines()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(result => {
        this.headerDropdownColorType = result.data.user.preferences?.headerDropdownColorType?.active?.value || 'status';
        const allParks = result.data.user.user_portfolios.flatMap(portfolio => portfolio.parks);
        const allTurbines = allParks.flatMap(park => park.turbines);
        this.parks.forEach(park => {
          const foundPark = allParks.find(p => p._id === park.id);
          park.status = foundPark?.data?.WTUR1_status?.min;
          park.load = foundPark?.data?.WTUR1_per_loadFactor?.avg || 0;
          park.efficiency = foundPark?.data?.WTUR1_per_efficiency?.avg || 0;
          park.name = park.frontEndCode;
        });

        this.turbines.forEach(turbine => {
          const foundTurbine = allTurbines.find(t => t._id === turbine._id);
          turbine.status = foundTurbine?.data?.WTUR1_status;
          turbine.load = foundTurbine?.data?.WTUR1_per_loadFactor || 0;
          turbine.efficiency = foundTurbine?.data?.WTUR1_per_efficiency || 0;
        });
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(true);
    this.unsubscribe$.complete();
  }

  isPortfolioActive(id) {
    return this.selectedLevel === 'portfolio' && this.selectedPortfolio?.id === id;
  }
  isParkActive(id) {
    return this.selectedLevel === 'park' && this.selectedPark.id === id;
  }
  isTurbineActive(id) {
    return this.selectedLevel === 'turbine' && this.selectedTurbine._id === id;
  }
  isTurbineActiveFromPark(id) {
    return this.selectedLevel === 'turbine' && this.selectedTurbine.id_windpark === id;
  }
  isFirstTurbineFromPark(id) {
    return this.selectedLevel === 'park' && this.firstTurbineFromPark === id;
  }
}
